import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { motion, useReducedMotion, type MotionStyle } from 'motion/react';
import { cn } from '@/lib/utils';
import { compose, usePressGesture, type GestureKind, type InvertRole } from './pressGesture';

/**
 * Button — one valid implementation of design/components/button.md.
 *
 * req 006 depth lock (step 7, H32): buttons are border-only and static at
 * rest; the *click* is the depth gesture (see ./press-gesture.ts). Press lifts
 * the button 1.5px and — for the chromatic roles — inverts it (fills the role
 * colour, snaps the border transparent, role-tinted-white text); release fades
 * back over a keyframed curve with a hold-at-full prefix. Neutral roles get the
 * same lift + a quiet accent fill; `secondary` (deferred) gets the lift only.
 * See design/exploration/008-req-006-formal-session.md §"Depth system".
 *
 * No `asChild`/Slot polymorphism (no consumer needs it today). If a `<Button
 * asChild>` case arises, re-vendor the Slot-based shadcn button and re-apply
 * the gesture.
 */

const buttonVariants = cva(
  // `select-none`: a button is a tap target, not text — a long-press (esp. on
  // touch) must not select the label (req 006, Ryan 2026-05-28). Applies to
  // every buttonVariants() consumer, including the Links/Radix actions styled
  // through it.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium select-none disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-ring shrink-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      // Border-only at rest (req 006 depth lock): no shadow, no hover. The
      // press gesture is the only depth a button carries. These classes are
      // the reduced-motion / pre-mount fallback appearance; for the gesture
      // path the motion `initial` rest colours (see press-gesture.ts) take
      // over. `outline` and `secondary` are both the neutral medium-weight
      // role and share the neutral invert.
      variant: {
        default: 'border border-primary bg-transparent text-primary',
        secondary: 'border border-input bg-transparent text-foreground',
        outline: 'border border-input bg-transparent text-foreground',
        ghost: 'border border-transparent bg-transparent text-foreground',
        destructive: 'border border-destructive bg-transparent text-destructive',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-10 rounded-md px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type Variant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

// CSS press-approximation of the H32 gesture for **link-as-button** affordances
// (the Sign-in CTA, the "← Records" back-link, the records-list filter pills).
// A TanStack <Link> must stay a real <a> with its route generics intact, and
// motion.create(Link) collides with those generics — so links can't carry the
// real motion gesture. They get this class instead: a brief invert + 1.5px
// lift on :active, transitioned, so a press is acknowledged with the same
// shape (fill + border-vanish + lift) the motion gesture uses on real Buttons.
// Promoting links to the full motion gesture needs a createLink-based custom
// link — tracked as a step-9 architectural follow-up.
const LINK_PRESS_BASE =
  'transition-[transform,background-color,border-color,color] duration-200 ease-linear active:-translate-y-[1.5px] active:border-transparent select-none';
const LINK_PRESS: Record<Variant, string> = {
  default: `${LINK_PRESS_BASE} active:bg-primary active:text-primary-foreground`,
  destructive: `${LINK_PRESS_BASE} active:bg-destructive active:text-white`,
  outline: `${LINK_PRESS_BASE} active:bg-foreground active:text-background`,
  secondary: `${LINK_PRESS_BASE} active:bg-foreground active:text-background`,
  ghost: `${LINK_PRESS_BASE} active:bg-accent active:text-accent-foreground`,
};

/**
 * Class for a TanStack `<Link>` that should look + press like a Button. Returns
 * `buttonVariants()` + the link press-approximation. Use on a real `<Link>` so
 * route typing stays intact: `<Link className={linkButtonClass('outline')} …>`.
 *
 * NOTE (integration): production consumers (C5 editor, C6 home/auth) have all
 * migrated to `<ButtonLink>`. The only remaining call site is the
 * `9b-divergences/nav-back-link` design-journey story, which uses it to render
 * the *rejected* louder candidates for comparison (the LOCK is a plain muted
 * text link). Deleting `linkButtonClass` is blocked on a design call about that
 * story — see the integration close-out.
 */
export function linkButtonClass(
  variant: Variant = 'default',
  size?: VariantProps<typeof buttonVariants>['size'],
  className?: string,
): string {
  return cn(buttonVariants({ variant, size }), LINK_PRESS[variant], className);
}

// Press feedback for the prefers-reduced-motion path (no lift, no fade —
// instant :active colour so the action is still acknowledged).
const REDUCED_ACTIVE: Record<Variant, string> = {
  default: 'active:bg-primary active:text-primary-foreground active:border-transparent',
  destructive: 'active:bg-destructive active:text-white active:border-transparent',
  outline: 'active:bg-foreground active:text-background active:border-transparent',
  secondary: 'active:bg-foreground active:text-background active:border-transparent',
  ghost: 'active:bg-foreground active:text-background',
};

export const VARIANT_GESTURE: Record<Variant, { kind: GestureKind; role?: InvertRole }> = {
  default: { kind: 'invert', role: 'primary' },
  destructive: { kind: 'invert', role: 'destructive' },
  outline: { kind: 'invert', role: 'neutral' },
  secondary: { kind: 'invert', role: 'neutral' },
  ghost: { kind: 'invert', role: 'ghost' },
};

// The press gesture is motion-owned, so the handful of DOM handlers motion
// redefines (drag pan + animation lifecycle) are omitted from the public API —
// a Button consumer never wires these, and keeping them would collide with
// motion's prop types at the motion.create() boundary.
interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
    >,
    VariantProps<typeof buttonVariants> {}

// Private base button — plain <button> over the cva matrix. motion.create()
// wraps this so the gesture can drive y / boxShadow / colours as inline style
// without the DOM-vs-motion prop-type friction of `motion.button`.
const BaseButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  ),
);
BaseButton.displayName = 'BaseButton';

const MotionBaseButton = motion.create(BaseButton);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size,
      type = 'button',
      style,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const prefersReduced = useReducedMotion();
    const resolved = (variant ?? 'default') as Variant;
    const { kind, role } = VARIANT_GESTURE[resolved];
    const gesture = usePressGesture(kind, role);

    if (prefersReduced) {
      return (
        <BaseButton
          ref={ref}
          type={type}
          variant={variant}
          size={size}
          className={cn(REDUCED_ACTIVE[resolved], className)}
          style={style}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...props}
        />
      );
    }

    const h = gesture.handlers;
    return (
      <MotionBaseButton
        ref={ref}
        type={type}
        variant={variant}
        size={size}
        className={cn(gesture.focusTintClass, className)}
        animate={gesture.animate}
        initial={gesture.initial}
        onPointerDown={compose(h.onPointerDown, onPointerDown)}
        onPointerUp={compose(h.onPointerUp, onPointerUp)}
        onPointerCancel={compose(h.onPointerCancel, onPointerCancel)}
        onPointerLeave={compose(h.onPointerLeave, onPointerLeave)}
        onKeyDown={compose(h.onKeyDown, onKeyDown)}
        onKeyUp={compose(h.onKeyUp, onKeyUp)}
        {...(style !== undefined ? { style: style as MotionStyle } : {})}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

// --- ButtonLink — a real TanStack <Link> that looks + PRESSES like a Button ---
//
// AF-6b (design/exploration/008 §"Frontend design-system readiness"): a TanStack
// `<Link>` styled with `buttonVariants()` gets the *look* of a button but not the
// H32 press gesture — the gesture is motion + state + handlers, not CSS, and the
// earlier `motion.create(Link)` attempt erased Link's route generics (`to`/`search`
// type-correlation). The sanctioned fix is `createLink` over a custom host: it
// re-applies full route typing at the call site while the host renders a `motion.a`
// that carries the real gesture (the same `usePressGesture` + `VARIANT_GESTURE` the
// `<Button>` uses, just on an anchor). The gesture's inline-style animation rides as
// the host's own `animate`/`initial` props — `createLink` never sees them (they're
// not link props), so there's no generic collision.
//
// `<ButtonLink>` supersedes the `linkButtonClass` CSS press-approximation (above)
// for production: every production consumer (C5 editor, C6 home/auth) now uses
// `<ButtonLink>`. `linkButtonClass` survives only for the `9b-divergences`
// design-journey story's rejected candidates; deleting it is blocked on a design
// call about that story.

interface ButtonLinkAnchorProps
  extends Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
    >,
    VariantProps<typeof buttonVariants> {}

// Private host wrapped by createLink. Mirrors Button's two-path render (reduced-
// motion → plain <a> + instant :active colour; full → motion.a + the gesture).
// Unlike Button, no `motion.create()` wrapper is needed — `motion.a` is a motion
// DOM component that accepts route/anchor props directly, so there's no DOM-vs-
// motion prop-type friction to dodge.
const ButtonLinkAnchor = React.forwardRef<HTMLAnchorElement, ButtonLinkAnchorProps>(
  (
    {
      className,
      variant = 'default',
      size,
      style,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const prefersReduced = useReducedMotion();
    const resolved = (variant ?? 'default') as Variant;
    const { kind, role } = VARIANT_GESTURE[resolved];
    const gesture = usePressGesture(kind, role);

    if (prefersReduced) {
      return (
        <a
          ref={ref}
          className={cn(buttonVariants({ variant, size }), REDUCED_ACTIVE[resolved], className)}
          style={style}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...props}
        />
      );
    }

    const h = gesture.handlers;
    return (
      <motion.a
        ref={ref}
        className={cn(buttonVariants({ variant, size }), gesture.focusTintClass, className)}
        animate={gesture.animate}
        initial={gesture.initial}
        onPointerDown={compose(h.onPointerDown, onPointerDown)}
        onPointerUp={compose(h.onPointerUp, onPointerUp)}
        onPointerCancel={compose(h.onPointerCancel, onPointerCancel)}
        onPointerLeave={compose(h.onPointerLeave, onPointerLeave)}
        onKeyDown={compose(h.onKeyDown, onKeyDown)}
        onKeyUp={compose(h.onKeyUp, onKeyUp)}
        {...(style !== undefined ? { style: style as MotionStyle } : {})}
        {...props}
      />
    );
  },
);
ButtonLinkAnchor.displayName = 'ButtonLinkAnchor';

// `createLink` is resolved lazily on first render, not at module-eval. Calling it
// at module scope would make merely *importing* button.tsx require `createLink` to
// exist on `@tanstack/react-router` — which breaks the route-screen tests that mock
// the router with a from-scratch object literal (they only stub the exports the
// screen uses). Deferring to first render keeps the import side-effect-free while
// the binding is created exactly once and cached.
let createdButtonLink: LinkComponent<typeof ButtonLinkAnchor> | null = null;
function getCreatedButtonLink(): LinkComponent<typeof ButtonLinkAnchor> {
  createdButtonLink ??= createLink(ButtonLinkAnchor);
  return createdButtonLink;
}

/**
 * A TanStack-Router link that looks + PRESSES like a Button — the real H32 press
 * gesture on a real `<a>`, route typing (`to`/`params`/`search`) intact.
 *
 * `<ButtonLink variant="outline" to="/">Sign in</ButtonLink>` is the wave-2
 * replacement for `<Link className={linkButtonClass('outline')} to="/">`. Exposes
 * `variant` + `size` (the `buttonVariants` cva) on top of the full TanStack Link
 * props. Honours `prefers-reduced-motion` (mirrors `Button`).
 *
 * When to reach for `<ButtonLink>` vs. `<Button asChild>` vs. raw `buttonVariants()`
 * is the AF-6 usage convention — design's bounce-back, not specified here.
 */
const ButtonLink: LinkComponent<typeof ButtonLinkAnchor> = (props) => {
  const Created = getCreatedButtonLink();
  return <Created {...props} />;
};

export { Button, ButtonLink, buttonVariants };
export type { ButtonProps };
